import moment, { Moment } from 'moment-timezone';
import {
  CAMPAIGN_TYPE_MENTOR,
  CAMPAIGN_TYPE_PROTEGE,
  CAMPAIGN_TYPE_STUDENTS,
  CAMPAIGN_TYPE_VOLUNTEERS,
  RoleNames,
} from '../../constants';
import { RegistrationStatus } from '../../constants/Registration';
import { Campaign, Contact, ProgramEvent, User } from '../../models/data';

export const STUDENT_CHECKBOXES_DISMISSAL_KEY = 'STUDENT_CHECKBOXES_IS_DISMISSED';
export const TRAINING_BACKGROUND_DISMISSAL_KEY = 'TRAINING_BACKROUND_IS_DISMISSED';

export const shouldShowBackgroundCheckAlert = (contact: Contact, now: Date = new Date()) => {
  if (!contact || !contact.bgCheckDate) {
    return true;
  }

  const todayMoment: Moment = moment(now).tz('UTC').startOf('day');
  const julyFirstDayOfYear: number = todayMoment.isLeapYear() ? 183 : 182;

  let earliestValidYear: number;

  if (todayMoment.dayOfYear() < julyFirstDayOfYear) {
    earliestValidYear = todayMoment.subtract(3, 'years').year();
  } else {
    earliestValidYear = todayMoment.subtract(2, 'years').year();
  }

  const earliestValidMoment = moment(earliestValidYear + '-07-01', 'YYYY-MM-DD')
    .tz('UTC')
    .startOf('day');
  const bgCheckMoment = moment(contact.bgCheckDate);

  return bgCheckMoment.isBefore(earliestValidMoment);
};

export const shouldShowTrainingAlert = (contact: Contact) => {
  if (!contact || !contact.trainingCompleted) {
    return true;
  }

  return false;
};

export const shouldShowPreAssessmentAlert = (campaign: Campaign, event?: ProgramEvent, now: Date = new Date()) => {
  return isWithinEventWindow(event, campaign.timezone, now);
};

export const shouldShowPostAssessmentAlert = (campaign: Campaign, event?: ProgramEvent, now: Date = new Date()) => {
  return isWithinEventWindow(event, campaign.timezone, now);
};

export const shouldShowProgramSurveyAlert = (campaign: Campaign, now: Date = new Date()) => {
  if (!campaign.fieldTripDate) {
    return false;
  }

  const timezone = campaign.timezone || 'UTC';
  const momentFieldTripDate = moment(campaign.fieldTripDate).tz(timezone);

  return momentFieldTripDate.isSame(now, 'day');
};

export const shouldShowReenrollmentAlert = (currentUser: User, registrationSubmitted: boolean) => {
  // Don't show alert if registration is still pending
  if (currentUser.registration?.status === RegistrationStatus.PENDING) {
    return false;
  }

  return currentUser.eligibleForReenrollment && !registrationSubmitted;
};

export const shouldShowSpringEnrollmentAlert = (currentUser: User, registrationSubmitted: boolean): boolean => {
  if (registrationSubmitted) {
    return false;
  }

  // Don't show alert if registration is still pending
  if (currentUser.registration?.status === RegistrationStatus.PENDING) {
    return false;
  }

  return currentUser.eligibleForSpringEnrollment;

  /**
   * ALL OF THIS HAS BEEN OFFLOADED TO THE BACKEND (RTSWS-403)
   */

  // // If user was created less than 10 minutes ago, don't show alert (RTSWS-403)
  // if (currentUser.createdAt) {
  //   const tenMinutesAgo = moment().subtract(10, 'minutes');
  //   if (moment(currentUser.createdAt).isAfter(tenMinutesAgo)) {
  //     return false;
  //   }
  // }

  // // Must be a student or volunteer
  // if (!userIsStudent(currentUser) && !userIsVolunteer(currentUser)) {
  //   return false;
  // }

  // // Must not be mentor interested
  // if (userIsMentorInterested(currentUser)) {
  //   return false;
  // }

  // if (campaign) {
  //   if (userIsStudent(currentUser)) {
  //     // Student campaigns must not be protege
  //     if (campaignIsProtege(campaign)) return false;
  //   } else if (userIsVolunteer(currentUser)) {
  //     // Volunteer campaigns must not be mentor
  //     if (campaignIsMentor(campaign)) return false;
  //   }

  //   // If campaign name is present, check if today is after October of the current campaign year
  //   if (campaign.name && !todayIsAfterOctoberOfCurrentCampaignYear(campaign)) {
  //     return false;
  //   }
  // } else {
  //   // If campaign is not present, check if today is before May 1 of the current year (RTSWS-403)
  //   if (!todayIsBeforeMayOfCurrentYear(campaign)) {
  //     return false;
  //   }
  // }
};

export const shouldShowSpringEnrollmentConfirmationAlert = (currentUser: User, campaign: Campaign) => {
  // Must be a student
  if (!userIsStudent(currentUser)) {
    return false;
  }

  // Must be mentor interested
  if (!userIsMentorInterested(currentUser)) {
    return false;
  }

  // If campaign is Protege, check if today is after campaign second volunteer shift
  if (campaignIsProtege(campaign) && todayIsAfterTheCampaignSecondVolunteerShift(campaign)) {
    return false;
  }

  return true;
};

export const userIsStudent = (currentUser?: User | null) => {
  return currentUser?.role.name === RoleNames.STUDENT;
};

export const userRegistrationIsPending = (currentUser?: User | null) => {
  return currentUser?.registration?.status === RegistrationStatus.PENDING;
};

export const userIsVolunteer = (currentUser?: User | null) => {
  return currentUser?.role.name === RoleNames.VOLUNTEER;
};

export const userIsMentorInterested = (currentUser: User) => {
  return currentUser.contact?.mentorInterested === 'Yes';
};

export const todayIsFallEnrollmentPeriod = (now: Date = new Date()) => {
  return todayIsAfterMayOfCurrentYear(now) && !todayIsAfterOctoberOfCurrentSchoolYear(now);
};

export const todayIsSpringEnrollmentPeriod = (now: Date = new Date()) => {
  return todayIsBetweenNovemberAndApril(now);
};

/**
 * Is the current month one of the following months:
 * Nov, Dec, Jan, Feb, Mar, Apr, May
 */
export const todayIsAfterOctoberOfCurrentSchoolYear = (now: Date = new Date()) => {
  const timezone = 'UTC';
  const momentToday = moment(now).tz(timezone);
  const acceptableMonths = [0, 1, 2, 3, 4, 10, 11];

  return acceptableMonths.includes(momentToday.month());
};

/**
 * Is the current month one of the following months:
 * Nov, Dec, Jan, Feb, Mar, Apr
 */
export const todayIsBetweenNovemberAndApril = (now: Date = new Date()) => {
  const timezone = 'UTC';
  const momentToday = moment(now).tz(timezone);
  const acceptableMonths = [0, 1, 2, 3, 10, 11];

  return acceptableMonths.includes(momentToday.month());
};

// export const todayIsBeforeAprilOfNextYear = () => {
//   const timezone = 'UTC';
//   const currentYear = moment().tz(timezone).year();
//   const momentNextYearAprilFirst = moment(`${currentYear + 1}-04-01`).tz(timezone);
//   const momentToday = moment().tz(timezone);

//   return momentNextYearAprilFirst.isAfter(momentToday);
// };

export const todayIsBeforeAprilOfCurrentYear = (now: Date = new Date()) => {
  const timezone = 'UTC';
  const currentYear = moment(now).tz(timezone).year();
  const momentCurrentYearAprilFirst = moment(`${currentYear}-04-01`).tz(timezone);
  const momentToday = moment(now).tz(timezone);

  return momentCurrentYearAprilFirst.isAfter(momentToday);
};

export const todayIsAfterMayOfCurrentYear = (now: Date = new Date()) => {
  const timezone = 'UTC';
  const currentYear = moment(now).tz(timezone).year();
  const momentCurrentYearJuneFirst = moment(`${currentYear}-06-01`).tz(timezone);
  const momentToday = moment(now).tz(timezone);
  return momentCurrentYearJuneFirst.isSameOrBefore(momentToday);
};

const todayIsBeforeMayOfCurrentYear = (campaign: Campaign | null) => {
  const timezone = campaign && campaign.timezone ? campaign.timezone : 'UTC';
  const currentYear = moment().tz(timezone).year();
  const momentCurrentYearMayFirst = moment(`${currentYear}-05-01`).tz(timezone);
  const momentToday = moment().tz(timezone);

  return momentCurrentYearMayFirst.isAfter(momentToday);
};

// const todayIsAfterMayOfCurrentYear = (campaign: Campaign | null) => {
//   const timezone = campaign && campaign.timezone ? campaign.timezone : 'UTC';
//   const currentYear = moment().tz(timezone).year();
//   const momentCurrentYearJuneFirst = moment(`${currentYear}-06-01`).tz(timezone);
//   const momentToday = moment().tz(timezone);

export const campaignIsStudents = (campaign: Campaign) => {
  return getCampaignNameElement(campaign, 'program') === CAMPAIGN_TYPE_STUDENTS;
};

export const campaignIsVolunteers = (campaign: Campaign) => {
  return getCampaignNameElement(campaign, 'program') === CAMPAIGN_TYPE_VOLUNTEERS;
};

export const campaignIsProtege = (campaign: Campaign) => {
  return getCampaignNameElement(campaign, 'program') === CAMPAIGN_TYPE_PROTEGE;
};

export const campaignIsMentor = (campaign: Campaign) => {
  return getCampaignNameElement(campaign, 'program') === CAMPAIGN_TYPE_MENTOR;
};

export const todayIsAfterTheCampaignSecondVolunteerShift = (campaign: Campaign, now: Date = new Date()) => {
  const timezone = campaign?.timezone || 'UTC';
  const momentToday = moment(now).tz(timezone);
  const unacceptableMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  return unacceptableMonths.includes(momentToday.month());
};

const getCampaignNameElement = (campaign: Campaign, element: string) => {
  const nameRegexPattern = /^(\w{4})\/(.+)\/(.+),.([SVPM]{1}\w+$)/i;
  const elements = campaign.name.match(nameRegexPattern);

  if (!elements) return '';

  switch (element) {
    case 'year':
      return elements[1] || '';
    case 'city':
      return elements[2] || '';
    case 'school':
      return elements[3] || '';
    case 'program':
      return elements[4] || '';
    default:
      break;
  }

  return '';
};

const isWithinEventWindow = (event?: ProgramEvent, timeZone?: string, now: Date = new Date()) => {
  if (!event) {
    return false;
  }

  const timezone = timeZone || 'UTC';
  const momentStartDateTime = moment(event.startDateTime).tz(timezone);
  const momentStartDateTimeMinus30Minutes = momentStartDateTime.clone().subtract(30, 'minutes');
  const momentStartDateTimePlus90Minutes = momentStartDateTime.clone().add(90, 'minutes');
  const momentNow = moment(now).tz(timezone);

  return (
    momentStartDateTimeMinus30Minutes.isSameOrBefore(momentNow) &&
    momentStartDateTimePlus90Minutes.isSameOrAfter(momentNow)
  );
};
